import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Alert,
  Stack,
  Divider,
  Link
} from '@mui/material';
import {
  CleaningServices as CleaningIcon,
  Nature as EcoIcon,
  Build as BuildIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ShoppingCart as ShoppingIcon,
  Security as SecurityIcon,
  LocalShipping as ShippingIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import PageLayout from '../components/PageLayout';
import SectionBlock from '../components/SectionBlock';
import Seo from '../components/Seo';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tools-tabpanel-${index}`}
      aria-labelledby={`tools-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const standardProducts = [
  {
    category: 'Multi-surface & Kitchens',
    items: [
      {
        name: 'All-purpose cleaner concentrate (alkaline, 1:32 dilution)',
        example: 'Simple Green Industrial Cleaner & Degreaser',
        source: 'Home Depot Pro / Amazon Business',
        description: 'Versatile cleaner for most household surfaces',
        links: [
          { name: 'Home Depot', url: 'https://www.google.com/search?q=Simple+Green+Industrial+Cleaner+Degreaser+site:homedepot.com' },
          { name: 'Walmart', url: 'https://www.google.com/search?q=Simple+Green+Industrial+Cleaner+Degreaser+site:walmart.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Simple+Green+Industrial+Cleaner+Degreaser+site:amazon.com' }
        ]
      },
      {
        name: 'Citrus or solvent-based degreaser (1:10–1:20)',
        example: 'Krud Kutter Original',
        source: 'Home Depot',
        description: 'For stove tops/hoods and heavy grease removal',
        links: [
          { name: 'Home Depot', url: 'https://www.google.com/search?q=Krud+Kutter+Original+degreaser+site:homedepot.com' },
          { name: 'Walmart', url: 'https://www.google.com/search?q=Krud+Kutter+Original+degreaser+site:walmart.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Krud+Kutter+Original+degreaser+site:amazon.com' }
        ]
      },
      {
        name: 'Dish soap',
        example: 'Dawn Professional Dishwashing Liquid',
        source: 'Walmart / Costco Business Center',
        description: 'For light wipe-downs and spot-treating',
        links: [
          { name: 'Walmart', url: 'https://www.google.com/search?q=Dawn+Professional+Dishwashing+Liquid+site:walmart.com' },
          { name: 'Home Depot', url: 'https://www.google.com/search?q=Dawn+Professional+Dishwashing+Liquid+site:homedepot.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Dawn+Professional+Dishwashing+Liquid+site:amazon.com' }
        ]
      }
    ]
  },
  {
    category: 'Bathrooms',
    items: [
      {
        name: 'Acidic descaler (citric/lactic or phosphoric)',
        example: 'CLR PRO Calcium, Lime & Rust Remover',
        source: 'Home Depot Pro',
        description: 'For hard water & soap scum removal',
        links: [
          { name: 'Home Depot', url: 'https://www.google.com/search?q=CLR+PRO+Calcium+Lime+Rust+Remover+site:homedepot.com' },
          { name: 'Walmart', url: 'https://www.google.com/search?q=CLR+PRO+Calcium+Lime+Rust+Remover+site:walmart.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=CLR+PRO+Calcium+Lime+Rust+Remover+site:amazon.com' }
        ]
      },
      {
        name: 'Disinfectant (quat-based or hydrogen-peroxide based)',
        example: 'Lysol IC Quaternary Disinfectant Cleaner',
        source: 'Staples',
        description: 'For toilet/touchpoints sanitization',
        links: [
          { name: 'Staples', url: 'https://www.google.com/search?q=Lysol+IC+Quaternary+Disinfectant+Cleaner+site:staples.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Lysol+IC+Quaternary+Disinfectant+Cleaner+site:amazon.com' },
          { name: 'Walmart', url: 'https://www.google.com/search?q=Lysol+IC+Quaternary+Disinfectant+Cleaner+site:walmart.com' }
        ]
      },
      {
        name: 'Non-abrasive cream cleanser',
        example: 'Bar Keepers Friend Soft Cleanser',
        source: 'Amazon / Walmart',
        description: 'For tubs/sinks without scratching',
        links: [
          { name: 'Walmart', url: 'https://www.google.com/search?q=Bar+Keepers+Friend+Soft+Cleanser+site:walmart.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Bar+Keepers+Friend+Soft+Cleanser+site:amazon.com' },
          { name: 'Target', url: 'https://www.google.com/search?q=Bar+Keepers+Friend+Soft+Cleanser+site:target.com' }
        ]
      }
    ]
  },
  {
    category: 'Glass & Finishes',
    items: [
      {
        name: 'Glass & mirror cleaner (ammonia-free preferred)',
        example: 'Windex Ammonia-Free',
        source: 'Walmart / Amazon',
        description: 'To protect tint/films on windows',
        links: [
          { name: 'Walmart', url: 'https://www.google.com/search?q=Windex+Ammonia+Free+Glass+Cleaner+site:walmart.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Windex+Ammonia+Free+Glass+Cleaner+site:amazon.com' },
          { name: 'Target', url: 'https://www.google.com/search?q=Windex+Ammonia+Free+Glass+Cleaner+site:target.com' }
        ]
      },
      {
        name: 'Stainless steel polish/cleaner',
        example: 'Weiman Stainless Steel Cleaner & Polish',
        source: 'Amazon / Target',
        description: 'For appliances and fixtures',
        links: [
          { name: 'Amazon', url: 'https://www.google.com/search?q=Weiman+Stainless+Steel+Cleaner+Polish+site:amazon.com' },
          { name: 'Target', url: 'https://www.google.com/search?q=Weiman+Stainless+Steel+Cleaner+Polish+site:target.com' },
          { name: 'Home Depot', url: 'https://www.google.com/search?q=Weiman+Stainless+Steel+Cleaner+Polish+site:homedepot.com' }
        ]
      },
      {
        name: 'Wood cleaner (pH-neutral; no silicone buildup)',
        example: 'Murphy Oil Soap',
        source: 'Walmart / Amazon',
        description: 'Safe for all wood finishes',
        links: [
          { name: 'Walmart', url: 'https://www.google.com/search?q=Murphy+Oil+Soap+Wood+Cleaner+site:walmart.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Murphy+Oil+Soap+Wood+Cleaner+site:amazon.com' },
          { name: 'Target', url: 'https://www.google.com/search?q=Murphy+Oil+Soap+Wood+Cleaner+site:target.com' }
        ]
      }
    ]
  },
  {
    category: 'Floors',
    items: [
      {
        name: 'Neutral-pH floor cleaner (1:64–1:128)',
        example: 'Zep Neutral pH Floor Cleaner (Concentrate)',
        source: 'Home Depot',
        description: 'Safe for LVP/tile/laminate floors',
        links: [
          { name: 'Home Depot', url: 'https://www.google.com/search?q=Zep+Neutral+pH+Floor+Cleaner+Concentrate+site:homedepot.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Zep+Neutral+pH+Floor+Cleaner+Concentrate+site:amazon.com' },
          { name: 'Walmart', url: 'https://www.google.com/search?q=Zep+Neutral+pH+Floor+Cleaner+Concentrate+site:walmart.com' }
        ]
      },
      {
        name: 'Grout/tile alkaline booster',
        example: 'OxiClean Versatile (oxygen)',
        source: 'Walmart',
        description: 'Powdered oxygen additive as needed',
        links: [
          { name: 'Walmart', url: 'https://www.google.com/search?q=OxiClean+Versatile+Stain+Remover+site:walmart.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=OxiClean+Versatile+Stain+Remover+site:amazon.com' },
          { name: 'Target', url: 'https://www.google.com/search?q=OxiClean+Versatile+Stain+Remover+site:target.com' }
        ]
      }
    ]
  }
];

const sensitiveProducts = [
  {
    category: 'Multi-surface & Kitchens',
    items: [
      {
        name: 'Plant-based all-purpose concentrate (1:32)',
        example: 'ECOS PRO All Purpose Cleaner (Safer Choice)',
        source: 'Amazon Business / Staples',
        description: 'Surfactant blend, fragrance-free',
        links: [
          { name: 'Amazon Business', url: 'https://www.google.com/search?q=ECOS+PRO+All+Purpose+Cleaner+Safer+Choice+site:amazon.com' },
          { name: 'Staples', url: 'https://www.google.com/search?q=ECOS+PRO+All+Purpose+Cleaner+Safer+Choice+site:staples.com' },
          { name: 'Walmart', url: 'https://www.google.com/search?q=ECOS+PRO+All+Purpose+Cleaner+Safer+Choice+site:walmart.com' }
        ]
      },
      {
        name: 'Enzyme cleaner',
        example: 'Biokleen Bac-Out',
        source: 'Amazon / Natural Grocers',
        description: 'Protein/food stains, odor neutralizing',
        links: [
          { name: 'Amazon', url: 'https://www.google.com/search?q=Biokleen+Bac+Out+Natural+Cleaner+site:amazon.com' },
          { name: 'Natural Grocers', url: 'https://www.google.com/search?q=Biokleen+Bac+Out+Natural+Cleaner+site:naturalgrocers.com' },
          { name: 'Walmart', url: 'https://www.google.com/search?q=Biokleen+Bac+Out+Natural+Cleaner+site:walmart.com' }
        ]
      },
      {
        name: 'Fragrance-free dish soap',
        example: 'Seventh Generation Free & Clear',
        source: 'Walmart / Amazon',
        description: 'Spot cleaning, light degrease',
        links: [
          { name: 'Walmart', url: 'https://www.google.com/search?q=Seventh+Generation+Free+Clear+Dish+Soap+site:walmart.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Seventh+Generation+Free+Clear+Dish+Soap+site:amazon.com' },
          { name: 'Target', url: 'https://www.google.com/search?q=Seventh+Generation+Free+Clear+Dish+Soap+site:target.com' }
        ]
      }
    ]
  },
  {
    category: 'Bathrooms',
    items: [
      {
        name: 'Citric or lactic acid descaler',
        example: 'Ecover Limescale Remover',
        source: 'Amazon',
        description: 'Soap scum, hard water deposits',
        links: [
          { name: 'Amazon', url: 'https://www.google.com/search?q=Ecover+Limescale+Remover+site:amazon.com' },
          { name: 'Target', url: 'https://www.google.com/search?q=Ecover+Limescale+Remover+site:target.com' },
          { name: 'Walmart', url: 'https://www.google.com/search?q=Ecover+Limescale+Remover+site:walmart.com' }
        ]
      },
      {
        name: 'Hydrogen-peroxide disinfectant (0.5–3% RTU)',
        example: 'Diversey Oxivir 1 RTU (AHP)',
        source: 'Staples / Grainger',
        description: 'Touchpoints sanitization',
        links: [
          { name: 'Staples', url: 'https://www.google.com/search?q=Diversey+Oxivir+1+RTU+AHP+site:staples.com' },
          { name: 'Grainger', url: 'https://www.google.com/search?q=Diversey+Oxivir+1+RTU+AHP+site:grainger.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Diversey+Oxivir+1+RTU+AHP+site:amazon.com' }
        ]
      },
      {
        name: 'Baking soda',
        example: 'Arm & Hammer Baking Soda',
        source: 'Walmart / Costco',
        description: 'Gentle abrasive paste with AP cleaner',
        links: [
          { name: 'Walmart', url: 'https://www.google.com/search?q=Arm+Hammer+Baking+Soda+bulk+site:walmart.com' },
          { name: 'Costco', url: 'https://www.google.com/search?q=Arm+Hammer+Baking+Soda+bulk+site:costco.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Arm+Hammer+Baking+Soda+bulk+site:amazon.com' }
        ]
      }
    ]
  },
  {
    category: 'Glass & Finishes',
    items: [
      {
        name: 'Alcohol-free glass cleaner',
        example: 'Seventh Generation Free & Clear Glass Cleaner',
        source: 'Walmart / Amazon',
        description: 'Or distilled water + AP concentrate',
        links: [
          { name: 'Walmart', url: 'https://www.google.com/search?q=Seventh+Generation+Free+Clear+Glass+Cleaner+site:walmart.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Seventh+Generation+Free+Clear+Glass+Cleaner+site:amazon.com' },
          { name: 'Target', url: 'https://www.google.com/search?q=Seventh+Generation+Free+Clear+Glass+Cleaner+site:target.com' }
        ]
      },
      {
        name: 'Food-grade mineral oil',
        example: 'Howard Cutting Board Oil',
        source: 'Home Depot / Amazon',
        description: 'Tiny amount for stainless finish',
        links: [
          { name: 'Home Depot', url: 'https://www.google.com/search?q=Howard+Cutting+Board+Oil+site:homedepot.com' },
          { name: 'Amazon', url: 'https://www.google.com/search?q=Howard+Cutting+Board+Oil+site:amazon.com' },
          { name: 'Walmart', url: 'https://www.google.com/search?q=Howard+Cutting+Board+Oil+site:walmart.com' }
        ]
      },
      {
        name: 'pH-neutral wood cleaner, unscented',
        example: 'Bona Free & Simple Hardwood Floor Cleaner',
        source: 'Amazon / Lowe\'s',
        description: 'Safe for sensitive environments',
        links: [
          { name: 'Amazon', url: 'https://www.google.com/search?q=Bona+Free+Simple+Hardwood+Floor+Cleaner+site:amazon.com' },
          { name: 'Lowe\'s', url: 'https://www.google.com/search?q=Bona+Free+Simple+Hardwood+Floor+Cleaner+site:lowes.com' },
          { name: 'Home Depot', url: 'https://www.google.com/search?q=Bona+Free+Simple+Hardwood+Floor+Cleaner+site:homedepot.com' }
        ]
      }
    ]
  },
  {
    category: 'Floors',
    items: [
      {
        name: 'Neutral-pH, bio-certified floor cleaner (1:64–1:128)',
        example: 'ECOS PRO Neutral Floor Cleaner',
        source: 'Amazon Business / Staples',
        description: 'Eco-friendly floor cleaning',
        links: [
          { name: 'Amazon Business', url: 'https://www.google.com/search?q=ECOS+PRO+Neutral+Floor+Cleaner+site:amazon.com' },
          { name: 'Staples', url: 'https://www.google.com/search?q=ECOS+PRO+Neutral+Floor+Cleaner+site:staples.com' },
          { name: 'Walmart', url: 'https://www.google.com/search?q=ECOS+PRO+Neutral+Floor+Cleaner+site:walmart.com' }
        ]
      },
      {
        name: 'Stone-safe cleaner (no acids)',
        example: 'Black Diamond Stoneworks Marble & Tile Cleaner',
        source: 'Amazon',
        description: 'For natural stone surfaces',
        links: [
          { name: 'Amazon', url: 'https://www.google.com/search?q=Black+Diamond+Stoneworks+Marble+Tile+Cleaner+site:amazon.com' },
          { name: 'Home Depot', url: 'https://www.google.com/search?q=Black+Diamond+Stoneworks+Marble+Tile+Cleaner+site:homedepot.com' },
          { name: 'Walmart', url: 'https://www.google.com/search?q=Black+Diamond+Stoneworks+Marble+Tile+Cleaner+site:walmart.com' }
        ]
      }
    ]
  }
];

const coreTools = [
  {
    category: 'Cleaning Caddy & Cloths',
    items: [
      'Cleaning caddy (rigid, with handle)',
      'Microfiber cloths (10–12 total): 3× blue (glass/mirrors), 3× green (kitchen), 3× yellow (general), 2× red (toilets)',
      'Non-scratch scrub pads (2–3) + magic eraser (1–2)',
      'Detail brushes: 1 grout brush, 1 toothbrush-style, 1 bottle-neck brush'
    ]
  },
  {
    category: 'Cleaning Tools',
    items: [
      'Extendable duster (microfiber head, washable)',
      'Toilet bowl brush (job-only, in drip cup or disposable sleeve)',
      'Putty/razor scraper (with safety cover) for stuck gunk',
      'Squeegee (10–12") for glass/shower',
      'Spray bottles (labeled) x4 for solutions',
      'Measuring cup or squeeze-bottle for dilutions'
    ]
  },
  {
    category: 'Floor Equipment',
    items: [
      'HEPA vacuum (with crevice, dusting, upholstery tools)',
      'Flat mop with washable microfiber pads (2–3 per job)',
      'Bucket (compact, fits in car) or onboard spray reservoir'
    ]
  },
  {
    category: 'Safety & Setup',
    items: [
      'Nitrile gloves (powder-free)',
      'Knee pads (optional but recommended)',
      'Microfiber hand towel (dry hands/equipment)',
      'Contractor trash bags (2–3) + small bin liners',
      'Step stool (2-step, foldable) if reachable items expected',
      'Painter\'s tape (masking notes, protecting edges)',
      'Phone charger/power bank (for job app/photos)'
    ]
  },
  {
    category: 'Bring When Needed',
    items: [
      'Lint roller (hair on fabrics)',
      'Long bottle brush (water bottles/vents)',
      'Grout steam brush or handheld steamer (heavily soiled grout)',
      'Extension cord (10–15 m, grounded) for vac in larger homes'
    ]
  }
];

const ToolsAndProducts = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const renderProductCategory = (category) => (
    <Card key={category.category} sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          {category.category}
        </Typography>
        <Grid container spacing={2}>
          {category.items.map((item, idx) => (
            <Grid item xs={12} key={idx}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 1 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <CheckIcon color="success" sx={{ mt: 0.5, flexShrink: 0 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {item.description}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                      <Chip 
                        label={`Example: ${item.example}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip 
                        label={`Source: ${item.source}`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        icon={<ShoppingIcon />}
                      />
                    </Stack>
                    {item.links && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Shop Online:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {item.links.map((link, linkIdx) => (
                            <Link
                              key={linkIdx}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              <Chip
                                label={link.name}
                                size="small"
                                color="info"
                                variant="outlined"
                                icon={<OpenInNewIcon />}
                                sx={{ cursor: 'pointer' }}
                              />
                            </Link>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  const renderToolCategory = (category) => (
    <Accordion key={category.category}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <BuildIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {category.category}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <List dense>
          {category.items.map((item, idx) => (
            <ListItem key={idx}>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary={item}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <PageLayout>
      <Seo
        title="Recommended Cleaning Tools & Products"
        description="The cleaning tools and products our professional cleaners actually use and recommend for keeping your home spotless."
        path="/tools-and-products"
      />
      <SectionBlock sx={{
        py: 8,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <Container maxWidth="lg">
          <Stack spacing={3} alignItems="center" textAlign="center">
            <CleaningIcon sx={{ fontSize: 60, opacity: 0.9 }} />
            <Typography variant="h2" component="h1" fontWeight={700}>
              Professional Cleaning Tools & Products
            </Typography>
            <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: '800px' }}>
              Comprehensive guide to recommended products and essential tools for professional cleaning services
            </Typography>
          </Stack>
        </Container>
      </SectionBlock>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} centered>
            <Tab 
              label="Standard Cleaning Kit" 
              icon={<CleaningIcon />}
              iconPosition="start"
            />
            <Tab 
              label="Sensitive/Bio Kit" 
              icon={<EcoIcon />}
              iconPosition="start"
            />
            <Tab 
              label="Core Tools" 
              icon={<BuildIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Stack spacing={3}>
            <Alert severity="info" icon={<CleaningIcon />}>
              <Typography variant="h6" gutterBottom>
                Standard Cleaning Kit
              </Typography>
              <Typography variant="body2">
                For general households and heavy soil removal. These products provide excellent cleaning power 
                for most residential and commercial cleaning tasks.
              </Typography>
            </Alert>

            {/* Featured Products Quick Links */}
            <Card sx={{ bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
              <CardContent>
                <Typography variant="h6" color="info.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingIcon />
                  Quick Shop - Popular Products
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Get started quickly with these essential cleaning products from major retailers:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom color="primary.main">
                      🏠 Home Depot
                    </Typography>
                    <Stack spacing={1}>
                      <Link href="https://www.google.com/search?q=cleaning+supplies+professional+site:homedepot.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'primary.main' }}>
                        <Chip label="Cleaning Supplies" size="small" color="primary" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                      <Link href="https://www.google.com/search?q=cleaning+tools+professional+site:homedepot.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'primary.main' }}>
                        <Chip label="Cleaning Tools" size="small" color="primary" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom color="success.main">
                      🛒 Walmart
                    </Typography>
                    <Stack spacing={1}>
                      <Link href="https://www.google.com/search?q=cleaning+supplies+professional+site:walmart.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'success.main' }}>
                        <Chip label="Cleaning Supplies" size="small" color="success" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                      <Link href="https://www.google.com/search?q=cleaning+tools+professional+site:walmart.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'success.main' }}>
                        <Chip label="Cleaning Tools" size="small" color="success" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom color="warning.main">
                      📦 Amazon
                    </Typography>
                    <Stack spacing={1}>
                      <Link href="https://www.google.com/search?q=cleaning+supplies+professional+site:amazon.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'warning.main' }}>
                        <Chip label="Professional Supplies" size="small" color="warning" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                      <Link href="https://www.google.com/search?q=cleaning+tools+professional+site:amazon.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'warning.main' }}>
                        <Chip label="Professional Tools" size="small" color="warning" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                    </Stack>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  💡 <strong>Pro Tip:</strong> Look for bulk sizes and professional-grade products for better value. Many retailers offer business accounts with additional discounts.
                </Typography>
              </CardContent>
            </Card>
            
            {standardProducts.map(renderProductCategory)}
            
            <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <SecurityIcon />
                  <Typography variant="h6">
                    Extras & Tools
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Essential Extras:
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="Color-coded microfiber packs (glass, bath, kitchen, general)" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Non-scratch scrub pads + grout brush" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="PPE: nitrile gloves, splash-proof goggles" />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Additional Items:
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="HEPA vacuum bags/filters" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Odor neutralizer (enzyme-based; use sparingly)" />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Stack spacing={3}>
            <Alert severity="success" icon={<EcoIcon />}>
              <Typography variant="h6" gutterBottom>
                Sensitive / Bio Cleaning Kit
              </Typography>
              <Typography variant="body2">
                For allergy-prone, asthma, babies & pets. Fragrance-free, dye-free, no aerosols, low-VOC. 
                Prefer concentrates with EPA Safer Choice / Green Seal / UL ECOLOGO labels.
              </Typography>
            </Alert>

            {/* Eco-Friendly Products Quick Links */}
            <Card sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
              <CardContent>
                <Typography variant="h6" color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EcoIcon />
                  Quick Shop - Eco-Friendly Products
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Find safe, eco-friendly cleaning products for sensitive environments:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom color="success.main">
                      🌱 Natural Grocers
                    </Typography>
                    <Stack spacing={1}>
                      <Link href="https://www.google.com/search?q=natural+cleaning+supplies+eco+friendly+site:naturalgrocers.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'success.main' }}>
                        <Chip label="Natural Cleaners" size="small" color="success" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                      <Link href="https://www.google.com/search?q=eco+friendly+cleaning+products+site:naturalgrocers.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'success.main' }}>
                        <Chip label="Eco-Friendly" size="small" color="success" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom color="info.main">
                      🏢 Staples Business
                    </Typography>
                    <Stack spacing={1}>
                      <Link href="https://www.google.com/search?q=professional+cleaning+supplies+site:staples.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'info.main' }}>
                        <Chip label="Business Supplies" size="small" color="info" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                      <Link href="https://www.google.com/search?q=eco+friendly+cleaning+products+site:staples.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'info.main' }}>
                        <Chip label="Eco-Friendly" size="small" color="info" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom color="warning.main">
                      🎯 Target
                    </Typography>
                    <Stack spacing={1}>
                      <Link href="https://www.google.com/search?q=cleaning+supplies+professional+site:target.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'warning.main' }}>
                        <Chip label="Cleaning Supplies" size="small" color="warning" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                      <Link href="https://www.google.com/search?q=eco+friendly+cleaning+products+site:target.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'warning.main' }}>
                        <Chip label="Eco-Friendly" size="small" color="warning" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                    </Stack>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  🌿 <strong>Eco Tip:</strong> Look for EPA Safer Choice, Green Seal, or UL ECOLOGO certifications. These products are tested and verified to be safer for people and the environment.
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
              <CardContent>
                <Typography variant="h6" color="success.main" gutterBottom>
                  General Principles
                </Typography>
                <Stack direction="row" flexWrap="wrap" spacing={1}>
                  <Chip label="Fragrance-free" color="success" size="small" />
                  <Chip label="Dye-free" color="success" size="small" />
                  <Chip label="No aerosols" color="success" size="small" />
                  <Chip label="Low-VOC" color="success" size="small" />
                  <Chip label="EPA Safer Choice" color="success" size="small" />
                  <Chip label="Green Seal" color="success" size="small" />
                  <Chip label="UL ECOLOGO" color="success" size="small" />
                </Stack>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Avoid bleach, ammonia, and quats where possible. Focus on plant-based and enzyme cleaners.
                </Typography>
              </CardContent>
            </Card>
            
            {sensitiveProducts.map(renderProductCategory)}
          </Stack>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Stack spacing={3}>
            <Alert severity="warning" icon={<BuildIcon />}>
              <Typography variant="h6" gutterBottom>
                Core Tools - What You Need to Bring
              </Typography>
              <Typography variant="body2">
                Essential equipment and tools for professional cleaning services. Organized by category for easy packing and preparation.
              </Typography>
            </Alert>

            {/* Tools & Equipment Quick Links */}
            <Card sx={{ bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
              <CardContent>
                <Typography variant="h6" color="warning.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BuildIcon />
                  Quick Shop - Tools & Equipment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Find professional cleaning tools and equipment from trusted suppliers:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom color="primary.main">
                      🏠 Home Depot Pro
                    </Typography>
                    <Stack spacing={1}>
                      <Link href="https://www.google.com/search?q=professional+cleaning+tools+site:homedepot.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'primary.main' }}>
                        <Chip label="Cleaning Tools" size="small" color="primary" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                      <Link href="https://www.google.com/search?q=professional+vacuum+cleaners+site:homedepot.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'primary.main' }}>
                        <Chip label="Vacuums" size="small" color="primary" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom color="info.main">
                      🏢 Grainger
                    </Typography>
                    <Stack spacing={1}>
                      <Link href="https://www.google.com/search?q=professional+cleaning+tools+equipment+site:grainger.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'info.main' }}>
                        <Chip label="Professional Tools" size="small" color="info" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                      <Link href="https://www.google.com/search?q=industrial+cleaning+equipment+site:grainger.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'info.main' }}>
                        <Chip label="Cleaning Equipment" size="small" color="info" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom color="warning.main">
                      📦 Amazon Business
                    </Typography>
                    <Stack spacing={1}>
                      <Link href="https://www.google.com/search?q=professional+cleaning+tools+site:amazon.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'warning.main' }}>
                        <Chip label="Professional Tools" size="small" color="warning" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                      <Link href="https://www.google.com/search?q=professional+cleaning+equipment+site:amazon.com" target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'warning.main' }}>
                        <Chip label="Cleaning Equipment" size="small" color="warning" variant="outlined" icon={<OpenInNewIcon />} sx={{ cursor: 'pointer' }} />
                      </Link>
                    </Stack>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  🛠️ <strong>Tool Tip:</strong> Invest in quality tools that last. Look for professional-grade equipment with warranties. Consider buying in bulk for better pricing.
                </Typography>
              </CardContent>
            </Card>
            
            {coreTools.map(renderToolCategory)}
            
            <Card sx={{ bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <ShippingIcon color="warning" />
                  <Typography variant="h6" color="warning.main">
                    Pro Tips for Tool Management
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Organization:
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Use color-coded system for cloths and tools"
                          secondary="Prevents cross-contamination between areas"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Pack tools in order of use"
                          secondary="Start with general cleaning, end with bathrooms"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Maintenance:
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Clean and sanitize tools between jobs"
                          secondary="Prevents spreading germs and maintains professionalism"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Replace consumables regularly"
                          secondary="Microfiber cloths, scrub pads, vacuum filters"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        </TabPanel>

      </Container>
    </PageLayout>
  );
};

export default ToolsAndProducts;